import { useState } from "react";
import { getAbsoluteUrl } from "../../../utils/getAbsoluteUrl";

export interface Pressrelease {
  id: number;
  url: string;
  title: string;
  summary: string | null;
  imageUrl: string | null;
  publishedAt: string | null;
}

export interface APIResult {
  totalItems: number;
  start: number;
  amount: number;
  items: Pressrelease[];
  availableYears: number[];
}

export const usePressreleases = (itemsPerPage = 12) => {
  const [pressreleases, setPressreleases] = useState<Pressrelease[]>([]);
  const [currentPageLoaded, setCurrentPageLoaded] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  const getPageNumber = async (requestedPage = 1, year = 0, month = 0, searchText = '') => {
    const isReset = requestedPage === 1;
    // On a fresh load or filter reset start from 0; when deep-linking to page N on first load
    // (currentPageLoaded === 0 && requestedPage > 1) fetch all pages at once.
    const isColdDeepLink = currentPageLoaded === 0 && requestedPage > 1;
    const existingCount = isReset || isColdDeepLink ? 0 : currentPageLoaded * itemsPerPage;
    const fetchAmount = isColdDeepLink ? itemsPerPage * requestedPage : itemsPerPage;

    if (isReset || currentPageLoaded < requestedPage) {
      setLoading(true);
      const result = await fetchPressreleases(existingCount, fetchAmount, year, month, searchText);

      if (!result) {
        console.error("Failed to load pressreleases.");
        setLoading(false);
        return;
      }

      const newItems = isReset ? result.items : [...pressreleases, ...result.items];
      setHasMore(newItems.length < result.totalItems);
      setCurrentPageLoaded(requestedPage);
      setPressreleases(newItems);
      if (result.availableYears?.length > 0) {
        setAvailableYears(result.availableYears);
      }
      setLoading(false);
    }
  };

  const fetchPressreleases = async (
    start: number,
    amount: number,
    year: number,
    month: number,
    searchText: string
  ): Promise<APIResult | null> => {
    try {
      const urlBase = getAbsoluteUrl();
      const params = new URLSearchParams({
        start: String(start),
        amount: String(amount),
        ...(year > 0 && { year: String(year) }),
        ...(month > 0 && { month: String(month) }),
        ...(searchText && { searchText }),
      });
      const response = await fetch(`${urlBase}GetPressreleases?${params}`);
      return await response.json();
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  return { pressreleases, getPageNumber, availableYears, currentPageLoaded, loading, hasMore };
};
